/**
 * Money is always an integer in minor units (paisa) plus a currency. Never a float.
 * docs/05 § conventions.
 */

export type Currency = 'BDT';

export interface Money {
  amount_minor: number;
  currency: Currency;
}

/** Fulfilment modes an offering may declare (docs/03, docs/05 § fulfilment). */
export const FULFILMENT_MODES = ['manual', 'partner', 'api', 'licensed'] as const;
export type FulfilmentMode = (typeof FULFILMENT_MODES)[number];

/**
 * Regulatory rule, enforced in code (docs/05 § fulfilment):
 * when fulfilment is not 'licensed', the response MUST carry a partner_name and
 * the client MUST display it on every money screen. No UI string anywhere may
 * imply Oprappo is a bank, MFS operator or wallet issuer — the licence does not exist.
 */
export function requiresPartnerDisclosure(mode: FulfilmentMode): boolean {
  return mode !== 'licensed';
}

/**
 * Throws when a money-bearing payload omits a partner name it is required to carry.
 * Call this on the server before responding, and in the client before rendering.
 */
export function assertPartnerDisclosure(payload: {
  fulfilment: FulfilmentMode;
  partner_name: string | null;
}): void {
  if (requiresPartnerDisclosure(payload.fulfilment) && !payload.partner_name) {
    throw new Error(
      `fulfilment '${payload.fulfilment}' requires partner_name; only 'licensed' may omit it`,
    );
  }
}

/**
 * Format an amount for display: ৳ 1,190 — and ৳ 1,42,000, not ৳ 142,000.
 *
 * The locale is `en-IN` for its GROUPING, not its language. Bangladesh groups in
 * lakh and crore like the rest of South Asia: the design draws `৳ 1,42,000` and
 * `৳ 8,42,300`, and `en-US` would render those as `142,000` and `842,300`. That
 * would be wrong on every figure above one lakh, and wrong quietly — the digits
 * are all correct, only the commas move, which is exactly the kind of error that
 * survives review.
 *
 * `bn-BD` groups correctly but returns BANGLA digits (১,৪২,০০০), which is the wrong
 * fix: docs/02 § numerals keeps amounts in Latin digits set in Manrope with tabular
 * figures, because Manrope has no Bangla digits and falls back silently at the wrong
 * size. Bangla numerals belong in prose, set in Anek Bangla.
 *
 * This bug shipped in the first version of this file and was caught by a route
 * package transcribing a drawn total that disagreed with it.
 */
export function formatTaka(amountMinor: number): string {
  const taka = Math.round(amountMinor) / 100;
  const hasPaisa = Math.round(amountMinor) % 100 !== 0;
  return `৳ ${taka.toLocaleString('en-IN', {
    minimumFractionDigits: hasPaisa ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * The same amount in BANGLA digits: ৳ ৪২,১০,০০০.
 *
 * ---------------------------------------------------------------------------
 * THIS EXISTS BECAUSE TWO NORMATIVE SOURCES DISAGREE, AND THE DISAGREEMENT IS
 * NOT MINE TO SETTLE. It is recorded in docs/HANDOFF-LOG.md and needs a decision.
 *
 *   docs/06 § Bangla output quality:  "amounts stay ৳ 1,190 in Manrope with
 *                                      tabular figures"
 *   Oprappo P1 Founder Mode.dc.html:  every single drawn amount is Bangla —
 *                                      ৳ ৪২,১০,০০০, ৳ ২,১০,৫০০, ৳ ১,৮৪০.
 *                                      Latin-digit amounts in that file: zero.
 *
 * HANDOFF § 0.1 makes the .dc.html normative, so the founder surfaces follow the
 * drawing. But the AI-written founder report on the SAME SCREEN was formatting
 * through `formatTaka`, which produces Latin digits — so FM2 would have shown
 * ৳ ৪২,১০,০০০ in one panel and ৳ 42,10,000 in the next. One number, two scripts,
 * one screen. Whichever way the contradiction is finally resolved, that is wrong.
 *
 * So both functions exist and the founder surfaces use this one. Do not "unify"
 * them by deleting either until the docs/06-vs-design question has an answer.
 * ---------------------------------------------------------------------------
 *
 * A string produced here MUST be set in Anek Bangla, never Manrope — Manrope has
 * no Bangla digits and falls back silently at the wrong size (docs/02 § numerals).
 * That is the same rule that makes this function necessary rather than optional:
 * it is not a styling preference, it is which font can render the glyphs at all.
 */
export function formatTakaBn(amountMinor: number): string {
  return toBanglaDigits(formatTaka(amountMinor));
}

const BANGLA_DIGIT_CHARS = '০১২৩৪৫৬৭৮৯';

function toBanglaDigits(s: string): string {
  return s.replace(/[0-9]/g, (d) => BANGLA_DIGIT_CHARS.charAt(Number(d)));
}
